import turfContains from "@turf/boolean-contains";
import turfLineSplit from "@turf/line-split";
import turfFlatten from "@turf/flatten";
import turfUnion from "@turf/union";
import turfLength from "@turf/length";
import turfAlong from "@turf/along";
import turfDifference from "@turf/difference";
import turfPolygonToLine from "@turf/polygon-to-line";
import * as turf from "@turf/turf";
import { lineColor, polygonColor } from "../constants/colors";
import { vegetationAreas } from "../constants/vegetationAreas";
import { syncBuffersFor, removeBuffersForPolyId } from "./bufferUtils";
import { tagAndPaintAssetRisk, refreshAssetPaintOverlay, ensureAssetPaintOverlay, isValidPolygonGeom } from "./assetRiskUtils";
import { calculateResult, computeMaxAssetRiskFrom } from "./calculateResult";
import { debounce } from "./debounce";

const DEFAULT_BUFFERS = [1.5, 9, 30];

let globalLotPerimeterUpdateHandler = null;
let globalAssetContourUpdateHandler = null;
let globalHandlersInitialized = false;
let globalSelectionChangeHandler = null;
let _prevAssetSignatures = {};

export const registerDrawHandler = (
  map,
  draw,
  setResult,
  setDrawMode,
  whichMode = 'area',
  setRiskInfo = null,
  bufferDistances = [],
  setPolygon = null,
  setExpanded = null
) => {
  let cleanupFns = [];

  if (!globalHandlersInitialized) {
    initializeGlobalHandlers(map, draw, setResult, bufferDistances, setRiskInfo);
    globalHandlersInitialized = true;
  }

  if (whichMode === 'area') {
    const { onCreate, onDelete, destroy } = makeLotPerimeterHandler(map, draw, setResult, setDrawMode);

    const existingLotPerimeter = draw.getAll()?.features?.find(f => f?.properties?.lotPerimeter);
    if (existingLotPerimeter) {
      updateLineFromExistingPolygon(map, existingLotPerimeter, setResult);
    }

    map.on('draw.create', onCreate);
    map.on('draw.delete', onDelete);

    cleanupFns.push(() => {
      map.off('draw.create', onCreate);
      map.off('draw.delete', onDelete);
      destroy();
    });

    return () => cleanupFns.forEach(fn => fn());
  }

  if (whichMode === 'home') {
    const handleAssetCreate = (e) => {
      e.features?.forEach(async (poly) => {
        if (poly.geometry?.type === 'Polygon' && !poly.properties?.lotPerimeter) {
          const id = poly.id;
          const country = detectCountryForFeature(poly);
          const preset = COUNTRY_PRESETS[country] || DEFAULT_BUFFERS;

          draw.setFeatureProperty(id, 'country', country);
          draw.setFeatureProperty(id, 'buffers', [...preset]);
          draw.setFeatureProperty(id, 'assetContour', true);

          if (setPolygon) setPolygon(draw.get(id));
          if (setExpanded) setExpanded(true);

          await tagAndPaintAssetRisk(map, draw, draw.get(id), setRiskInfo);
          syncBuffersFor(draw.get(id), map, preset);


          try {
            const created = draw.get(id);
            const res = calculateResult(created);
            setResult?.(res);
          } catch { }

          // map.once('draw.render', () => draw.changeMode('direct_select', { featureId: id }));
        }
      });

      setDrawMode?.(prev => ({ ...prev, home: 'simple_select' }));
    };

    map.on('draw.create', handleAssetCreate);
    cleanupFns.push(() => map.off('draw.create', handleAssetCreate));
  }

  if (whichMode === 'point') {
    const handlePoint = () => setDrawMode?.(prev => ({ ...prev, point: 'simple_select' }));
    map.on('draw.create', handlePoint);
    cleanupFns.push(() => map.off('draw.create', handlePoint));
  }

  return () => cleanupFns.forEach(fn => fn());
};

const initializeGlobalHandlers = (map, draw, setResult, bufferDistances, setRiskInfo) => {
  if (!globalLotPerimeterUpdateHandler) {
    globalLotPerimeterUpdateHandler = (e) => {
      const lotPerimeterFeature = e.features?.find(f => f?.properties?.lotPerimeter);
      if (lotPerimeterFeature) {
        updateLineFromExistingPolygon(map, lotPerimeterFeature, setResult);
      }
    };

    map.on('draw.update', globalLotPerimeterUpdateHandler);
    map.on('draw.render', globalLotPerimeterUpdateHandler);
  }

  if (!globalAssetContourUpdateHandler) {
    globalAssetContourUpdateHandler = debounce((e) => {
      let features = Array.isArray(e?.features) && e.features.length ? e.features : [];

      if (!features.length) {
        const all = draw.getAll();
        features = Array.isArray(all?.features) ? all.features : [];
      }

      if (!features.length || features.some(item => Object.keys(item.properties).length === 0)) return;

      const currentSignatures = {};
      const assetFeatures = features.filter(f => f && (f.properties?.assetContour || f.properties?.isBuilding));

      assetFeatures.forEach(f => {
        const id = f.id;
        const latest = id ? (draw.get(id) || f) : f;
        const sigObj = {
          geometry: latest.geometry,
          buffers: latest.properties?.buffers || null,
          assetContour: !!latest.properties?.assetContour,
          isBuilding: !!latest.properties?.isBuilding,
        };
        currentSignatures[id] = JSON.stringify(sigObj);
      });

      const changedIds = [];
      const prevIds = Object.keys(_prevAssetSignatures || {});
      const currIds = Object.keys(currentSignatures);

      prevIds.forEach(pid => { if (!currIds.includes(pid)) changedIds.push(pid); });
      currIds.forEach(cid => {
        if (_prevAssetSignatures[cid] !== currentSignatures[cid]) changedIds.push(cid);
      });

      if (!changedIds.length) {
        return;
      }

      changedIds.forEach(id => {
        const raw = features.find(f => f && f.id === id) || draw.get(id)
        if (!raw) return;
        const latest = raw.id ? (draw.get(raw.id) || raw) : raw;

        const polyBuffers = latest.properties?.buffers || bufferDistances;
        tagAndPaintAssetRisk(map, draw, latest, setRiskInfo);
        syncBuffersFor(latest, map, polyBuffers);

        const res = calculateResult(latest);
        setResult?.(res);
      });

      _prevAssetSignatures = { ...currentSignatures };

      refreshAssetPaintOverlay(map, draw);
    }, 300);

    map.on('draw.update', globalAssetContourUpdateHandler);
    map.on('draw.render', globalAssetContourUpdateHandler);
  }

  if (!globalSelectionChangeHandler) {
    const live = (f) => (typeof draw.get === 'function' ? (draw.get(f.id) || f) : f);

    globalSelectionChangeHandler = (e) => {
      const feats = e?.features || [];
      const selected = [...feats]
        .reverse()
        .find(f =>
          (f?.properties?.assetContour || f?.properties?.isBuilding) &&
          (f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'MultiPolygon')
        );

      if (!selected) return;

      try {
        const latest = live(selected);
        const res = calculateResult(latest);
        setResult?.(res);
      } catch { }
    };

    map.on('draw.selectionchange', globalSelectionChangeHandler);
  }

  const globalDeleteHandler = (e) => {
    e.features?.forEach(f => {
      if (f.properties?.assetContour || f.properties?.isBuilding) {
        removeBuffersForPolyId(map, f.id);
        const info = computeMaxAssetRiskFrom(draw);
        setRiskInfo(info);
      }
    });
  };

  ensureAssetPaintOverlay(map);
  refreshAssetPaintOverlay(map, draw);

  map.on('draw.delete', globalDeleteHandler);
};

export function makeLotPerimeterHandler(map, draw, setResult, setDrawMode) {
  let currentLotPerimeterId = null;
  let lineClickBound = false;
  let lineClickEnabled = true;

  const enter = () => map.getCanvas().style.cursor = 'pointer';
  const leave = () => map.getCanvas().style.cursor = '';

  const onLineClick = () => {
    if (!lineClickEnabled) return;

    const existingLotPerimeter = draw.getAll()?.features?.find(f => f?.properties?.lotPerimeter);
    if (existingLotPerimeter) {
      draw.changeMode('direct_select', { featureId: existingLotPerimeter.id });
      currentLotPerimeterId = existingLotPerimeter.id;
      ensureDrawAboveLine(map);
      lineClickEnabled = false;
      setTimeout(() => { lineClickEnabled = true; }, 300);
      return;
    }

    const ring = getCurrentLotRing(map);
    if (!ring || ring.length < 3) return;

    const poly = lineCoordsToPolygonFeature(ring, {
      lotPerimeter: true,
      scope: 'area',
      assetContour: false
    });

    const added = draw.add(poly);
    const polyId = Array.isArray(added) ? added[0] : added;
    currentLotPerimeterId = polyId;

    detachLineEvents();
    map.once('draw.render', () => {
      draw.changeMode('direct_select', { featureId: polyId });
      ensureDrawAboveLine(map);
      setTimeout(() => { lineClickEnabled = true; }, 300);
    });
  };

  const detachLineEvents = () => {
    if (!lineClickBound) return;
    if (map.getLayer('line')) {
      map.off('click', 'line', onLineClick);
      map.off('mouseenter', 'line', enter);
      map.off('mouseleave', 'line', leave);
    }
    lineClickBound = false;
  };

  const attachLineEvents = () => {
    if (lineClickBound || !map.getLayer('line')) return;
    if (!lineClickEnabled) return;
    map.on('click', 'line', onLineClick);
    map.on('mouseenter', 'line', enter);
    map.on('mouseleave', 'line', leave);
    lineClickBound = true;
  };

  const onCreate = (e) => {
    const f = e?.features?.[0];
    if (!f) return;

    if (f.properties?.lotPerimeter) {
      currentLotPerimeterId = f.id;
      updateLineFromExistingPolygon(map, f, setResult);
      return;
    }

    const ring = extractBaseRing(f.geometry);
    setCurrentLotRing(map, ring);

    updateLineFromExistingPolygon(map, { geometry: f.geometry }, setResult);
    attachLineEvents();

    draw.delete(f.id);
    setDrawMode?.(prev => ({ ...prev, area: 'simple_select' }));
  };

  const onDelete = (e) => {
    const deletedPerimeter = (e?.features || []).find(x => x.properties?.lotPerimeter);
    if (deletedPerimeter) {
      currentLotPerimeterId = null;
      lineClickEnabled = true;
      setTimeout(() => { if (map.getLayer('line')) attachLineEvents(); }, 100);
    }
  };

  const destroy = () => { lineClickEnabled = true; };

  map.on('draw.selectionchange', (e) => {
    if (!currentLotPerimeterId) return;
    const selectedIds = typeof draw.getSelectedIds === 'function'
      ? draw.getSelectedIds()
      : (e?.features || []).map(f => f.id);

    const stillSelected = selectedIds.includes(currentLotPerimeterId);
    if (stillSelected) return;

    safeDeleteLot(draw, map, currentLotPerimeterId);
    currentLotPerimeterId = null;

    if (map.getLayer('line')) attachLineEvents();
  });

  return { onCreate, onDelete, destroy };
}

export const updateLineFromExistingPolygon = (map, polygon, setResult) => {
  if (!polygon || !polygon.geometry) return;

  const ringForCache = extractBaseRing(polygon.geometry);
  setCurrentLotRing(map, ringForCache);

  const landcoverFeatures = map.queryRenderedFeatures({ layers: ['landcover-2'] });

  const isPoly = polygon.geometry?.type === 'Polygon' || polygon.geometry?.type === 'MultiPolygon';
  const baseLine = isPoly ? turfPolygonToLine({ type: 'Feature', geometry: polygon.geometry }) : { type: 'Feature', geometry: polygon.geometry };
  const lineColorFromMode = isPoly ? polygonColor : lineColor;

  // Process landcover features
  const grouped = landcoverFeatures.reduce((acc, f) => {
    const c = f?.properties?.class || 'other';
    if (!acc[c]) acc[c] = [];
    acc[c].push(f);
    return acc;
  }, {});

  const united = Object.values(grouped).map((arr) => {
    return arr.reduce((acc, f) => acc ? { ...turfUnion(acc, f), properties: f.properties } : f, null);
  });

  const cropped = united.map((f) => {
    const others = united.filter(o => o && o.properties?.class !== f.properties?.class);
    return others.reduce((acc, o) => turfDifference(acc, o) ?? acc, f);
  });

  const splittedLine = cropped.reduce((lines, landcover) => {
    return lines.flatMap(line => {
      if (line.properties?.class) return line;
      const splitted = turfLineSplit(line, landcover).features;
      const parts = (splitted.length > 0 ? splitted : [line]).map(seg => {
        const polygons = landcover.geometry.type === 'MultiPolygon' ? turfFlatten(landcover).features : [landcover];
        const len = turfLength(seg);
        const mid = turfAlong(seg, len / 2);
        if (polygons.some(p => turfContains(p, mid)) && seg.properties?.class !== 'snow') {
          seg.properties = { ...(seg.properties || {}), class: landcover.properties.class };
        }
        return seg;
      });
      return parts;
    });
  }, [baseLine]);

  const groupedLineDistance = splittedLine.reduce((acc, f) => {
    const c = f.properties?.class || 'other';
    acc[c] = (acc[c] || 0) + turfLength(f) * 1000;
    return acc;
  }, {});

  const distance = turfLength(baseLine) * 1000;

  const fc = { type: 'FeatureCollection', features: splittedLine };

  if (map.getSource('line')) {
    map.getSource('line').setData(fc);
  } else {
    map.addSource('line', { type: 'geojson', data: fc });
  }

  if (!map.getLayer('line')) {
    map.addLayer({
      id: 'line',
      source: 'line',
      type: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#FFF', 'line-width': 10 },
    });
  }

  if (!map.getLayer('line-outline')) {
    map.addLayer({
      id: 'line-outline',
      source: 'line',
      type: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': lineColorFromMode, 'line-width': 5 },
    });
  } else {
    map.setPaintProperty('line-outline', 'line-color', lineColorFromMode);
  }

  const sortedLineDistance = vegetationAreas.reduce((acc, obj) => {
    if (Object.prototype.hasOwnProperty.call(groupedLineDistance, obj.text)) {
      acc[obj.text] = groupedLineDistance[obj.text];
    }
    return acc;
  }, {});

  const percentage = {};
  Object.entries(groupedLineDistance).forEach(([k, v]) => percentage[k] = (v / distance) * 100);

  const calculateZoneScore = (pct) => {
    const { wood = 0, scrub = 0, crop = 0, grass = 0, other = 0 } = pct;
    let score = 1;
    switch (true) {
      case (wood !== 0 && scrub + wood > 30): score = 5; break;
      case (scrub > 50 && Math.round((crop + grass + other)) === Math.round((100 - scrub))): score = 4; break;
      case (scrub > 30 && Math.round((crop + grass + other)) === Math.round((100 - scrub))): score = 3; break;
      case (crop > 50 && Math.round((grass + other)) === Math.round((100 - crop))): score = 2; break;
      case (other === 100): score = 0; break;
      default: score = 1; break;
    }
    return score;
  };

  const zoneScore = calculateZoneScore(percentage);

};

// Helper functions
const setCurrentLotRing = (map, ring) => {
  map.__lotRing = Array.isArray(ring) ? ring.slice() : null;
};

const getCurrentLotRing = (map) => {
  return Array.isArray(map.__lotRing) ? map.__lotRing.slice() : null;
};

const extractBaseRing = (geom) => {
  if (!geom) return null;
  if (geom.type === 'Polygon') return (geom.coordinates?.[0] || []).slice();
  if (geom.type === 'MultiPolygon') return (geom.coordinates?.[0]?.[0] || []).slice();
  if (geom.type === 'LineString') return (geom.coordinates || []).slice();
  if (geom.type === 'MultiLineString') return (geom.coordinates?.[0] || []).slice();
  return null;
};

const lineCoordsToPolygonFeature = (coords, props = {}) => {
  if (!Array.isArray(coords) || coords.length < 3) return null;

  const ring = coords.slice();
  const [fx, fy] = ring[0];
  const [lx, ly] = ring[ring.length - 1];
  const isClosed = fx === lx && fy === ly;

  if (!isClosed) ring.push([fx, fy]);

  return {
    type: 'Feature',
    properties: { ...props },
    geometry: { type: 'Polygon', coordinates: [ring] }
  };
};

function safeDeleteLot(draw, map, lotId) {
  if (!lotId) return;

  try {
    const mode = typeof draw.getMode === 'function' ? draw.getMode() : null;
    if (mode?.name === 'direct_select' || mode?.mode === 'direct_select') {
      draw.changeMode('simple_select');
    }
  } catch { }

  setTimeout(() => {
    if (draw.get(lotId)) {
      draw.delete(lotId);
    }
  }, 0);
}

const ensureDrawAboveLine = (map) => {
  const layers = map.getStyle()?.layers || [];
  const beforeId = layers.find(x => x?.id?.startsWith('gl-draw-polygon'))?.id;

  if (!beforeId) return;
  if (map.getLayer('line')) map.moveLayer('line', beforeId);
  if (map.getLayer('line-outline')) map.moveLayer('line-outline', beforeId);
};

export const COUNTRY_PRESETS = {
  US: [1.5, 10, 30],
  GR: [2, 5, 10],
  FR: [10, 50],
};

const COUNTRY_BBOX = {
  GR: { minLon: 19.0, minLat: 34.6, maxLon: 28.5, maxLat: 41.9 },
  FR: { minLon: -5.6, minLat: 41.0, maxLon: 9.7, maxLat: 51.6 },
};

export const detectCountryForFeature = (feature) => {
  try {
    const [lon, lat] = turf.centroid(feature).geometry.coordinates;
    const inBox = (b) => lon >= b.minLon && lon <= b.maxLon && lat >= b.minLat && lat <= b.maxLat;

    if (inBox(COUNTRY_BBOX.GR)) return 'GR';
    if (inBox(COUNTRY_BBOX.FR)) return 'FR';
    return 'US';
  } catch {
    return 'US';
  }
};

export const clearAllHandlers = (map) => {
  if (!map) return;

  if (globalLotPerimeterUpdateHandler) {
    map.off('draw.update', globalLotPerimeterUpdateHandler);
    map.off('draw.render', globalLotPerimeterUpdateHandler);
    globalLotPerimeterUpdateHandler = null;
  }

  if (globalAssetContourUpdateHandler) {
    map.off('draw.update', globalAssetContourUpdateHandler);
    map.off('draw.render', globalAssetContourUpdateHandler);
    globalAssetContourUpdateHandler = null;
  }

  globalHandlersInitialized = false;
};

export function getLotPerimeterFeatureFromMap(map) {
  if (!map) return null;
  const ring = getCurrentLotRing(map);
  if (!Array.isArray(ring) || ring.length < 3) return null;

  const feat = lineCoordsToPolygonFeature(ring, {
    lotPerimeter: true,
    scope: 'area',
    assetContour: false,
    source: 'line'
  });
  return feat && isValidPolygonGeom(feat.geometry) ? feat : null;
}

export function buildGeoDataForSubmit(map, draw) {

  const features = [];

  const alreadyHasLot = features.some(f => f?.properties?.lotPerimeter === true);

  if (!alreadyHasLot) {
    const lot = getLotPerimeterFeatureFromMap(map);
    if (lot) {
      const {
        properties: { assetContour, ...restProps }, // вырезаем из properties
        ...restLot
      } = lot;

      const sanitized = { ...restLot, properties: { ...restProps } };
      features.push(sanitized)
    };
  }

  const base = draw.getAll();

  if (base && Array.isArray(base.features)) {
    features.push(...base.features)
  }

  return { type: 'FeatureCollection', features };
}